import { useEffect, useRef, useCallback } from 'react';
import { User } from "oidc-client-ts";

export default function idleLogoutTimer(onIdleFunc: () => void, idleTimeMs: number, user: User | null) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null); // Debug log. Can remove if not needed

  /* Sync the ref when the user object updates (user login/out & token refresh) */
  useEffect(() => {
    // Cleanup on user logout
    if (!user) {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
      timerRef.current = null;
      countdownIntervalRef.current = null;
    }
  }, [user]);

  /* Function to reset inactivity timer - use callback to avoid recreation of the function whenever user action is captured. */
  const resetTimer = useCallback(() => {
    // Cleanup existing timer before reset
    if (timerRef.current) clearTimeout(timerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);

    // Set new timer for logout (action).
    timerRef.current = setTimeout(() => {
      console.debug("***** Timeout - Logout and redirect to fi.se");
      onIdleFunc(); // execute handleLogoutCallback from app.tsx
    }, idleTimeMs);

    console.debug(`***** INACTIVITY TIMER RESET FOR ${idleTimeMs / 60000} MIN`);

    // Debug log - countdown timer
    let secondsRemaining = idleTimeMs / 1000;

    countdownIntervalRef.current = setInterval(() => {
      secondsRemaining -= 5;
      if (secondsRemaining > 0) {
        console.debug(`***** Inactivity Countdown: ${secondsRemaining}s remaining`);
      } else {
        if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
      }
    }, 5000);
  }, [onIdleFunc, idleTimeMs]); // dependencies for useCallback hook

  /* Debounce user activity tracing */
  const handleUserActivity = useCallback(() => {
    if (debounceRef.current) return;

    resetTimer();

    // Create a "lock" that lasts for 500ms
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null; // when 500ms is reached, callback runs and unlock.
    }, 500);
  }, [resetTimer]);

  /* Trace user activity and run handleUserActivity */
  useEffect(() => {
    if (!user) return;

    const events = ['mousemove', 'mousedown', 'keypress', 'touchstart'];

    events.forEach(event => window.addEventListener(event, handleUserActivity));

    // Initial mount
    resetTimer();

    // cleanup when user closes browser or refresh page
    return () => {
      events.forEach(event => window.removeEventListener(event, handleUserActivity));
      if (timerRef.current) clearTimeout(timerRef.current);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, [handleUserActivity, resetTimer, !!user]);
};
